import { getDeviceId } from '../utils/deviceId';

export interface TokenRefreshCallback {
  onTokenRefreshed: (newToken: string, newRefreshToken: string) => void;
  onTokenRefreshFailed: (error: string) => void;
}

export interface TokenProvider {
  getAuthToken: () => string | null;
  getRefreshToken: () => string | null;
  setAuthToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
  getBaseURL: () => string;
}

export class TokenManager {
  private static instance: TokenManager;
  private refreshCallbacks: TokenRefreshCallback[] = [];
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<{ token: string; refreshToken: string } | null> | null = null;
  private tokenProvider: TokenProvider | null = null;

  private constructor() {}

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  // Set the token provider (called by API service)
  setTokenProvider(provider: TokenProvider): void {
    this.tokenProvider = provider;
  }

  // Register callback for token refresh events
  registerCallback(callback: TokenRefreshCallback): void {
    this.refreshCallbacks.push(callback);
  }

  // Unregister callback
  unregisterCallback(callback: TokenRefreshCallback): void {
    const index = this.refreshCallbacks.indexOf(callback);
    if (index > -1) {
      this.refreshCallbacks.splice(index, 1);
    }
  }

  // Centralized token refresh method
  async refreshTokens(): Promise<{ token: string; refreshToken: string } | null> {
    if (this.isRefreshing && this.refreshPromise) {
      // If already refreshing, wait for the existing promise
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh();
    
    try {
      const result = await this.refreshPromise;
      
      if (result) {
        // Notify all callbacks about successful token refresh
        this.refreshCallbacks.forEach(callback => {
          try {
            callback.onTokenRefreshed(result.token, result.refreshToken);
          } catch (error) {
            console.error('Error in token refresh callback:', error);
          }
        });
      } else {
        // Notify all callbacks about failed token refresh
        this.refreshCallbacks.forEach(callback => {
          try {
            callback.onTokenRefreshFailed('Token refresh failed');
          } catch (error) {
            console.error('Error in token refresh failed callback:', error);
          }
        });
      }
      
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  // Perform the actual token refresh
  private async performTokenRefresh(): Promise<{ token: string; refreshToken: string } | null> {
    try {
      const refreshToken = this.tokenProvider?.getRefreshToken();
      if (!refreshToken) {
        return null;
      }

      // Call the refresh token endpoint. The backend resolves this device's trust level and
      // sliding-window length from the refresh token's own DB row, not from this header — but
      // we still send it so the newly-minted refresh token stays associated with this device.
      let deviceId: string | undefined;
      try {
        deviceId = await getDeviceId();
      } catch {
        // best-effort
      }
      const refreshResponse = await fetch(`${this.tokenProvider?.getBaseURL()}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(deviceId ? { 'X-Device-Id': deviceId } : {}),
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!refreshResponse.ok) {
        return null;
      }

      const refreshData = await refreshResponse.json();
      
      if (refreshData.statusCode === 1000 && refreshData.data?.token) {
        const newToken = refreshData.data.token;
        const newRefreshToken = refreshData.data.refreshToken || refreshToken;
        
        // Update API service tokens
        this.tokenProvider?.setAuthToken(newToken);
        this.tokenProvider?.setRefreshToken(newRefreshToken);
        
        return {
          token: newToken,
          refreshToken: newRefreshToken
        };
      } else {
        return null;
      }
      
    } catch (error) {
      return null;
    }
  }

  // Check if tokens are valid
  isTokenValid(): boolean {
    return !!this.tokenProvider?.getAuthToken();
  }

  // Get current auth token
  getAuthToken(): string | null {
    return this.tokenProvider?.getAuthToken() || null;
  }

  // Get current refresh token
  getRefreshToken(): string | null {
    return this.tokenProvider?.getRefreshToken() || null;
  }
}

// Export singleton instance
export const tokenManager = TokenManager.getInstance();
